<template lang="pug">
  #risk-view.body: .body-inner
    //- Risk type --------------------------------------------------------------
    h4.risk-type.no-select
      .title(v-text="text.typeTitle")
      .value(v-text="curRiskType")
    //- Risk -------------------------------------------------------------------
    .risk.no-select
      .risk-top-labels
        .label-left(v-text="text.labels.top[0]")
        .label-right(v-text="text.labels.top[1]")
      .risk-slider(ref="riskSliderElement")
      .risk-bottom-labels
        .label-left(v-text="text.labels.bottom[0]")
        .label-right(v-text="text.labels.bottom[1]")
    //- Only bonds -------------------------------------------------------------
    .only-bonds.no-select
      input(
        type="checkbox"
        id="only-bonds"
        v-model="checkOnlyBondsModel"
      )
      label(for="only-bonds"): UiIcon(icon="ui-icon-check")
      h4.title(v-text="text.onlyBonds")
      .help: UiTooltip(:content="tooltips.onlyBonds" theme="light")
    //- Risk values ------------------------------------------------------------
    .risk-values.no-select
      .values-row
        .title(v-text="text.values[0]")
        .help: UiTooltip(:content="tooltips.tpPRoL" theme="light")
        .value
          i: UiIcon(icon="ui-icon-arrow")
          | {{ calcPRoL + '%' }}
      .values-row
        .title(v-text="text.values[1]")
        .help: UiTooltip(:content="tooltips.tpPRoR" theme="light")
        .value
          i: UiIcon(icon="ui-icon-arrow")
          | {{ calcPRoR + '%' }}
</template>

<script>
import { _cfg } from '@/config'
import roundToOne from '@/utils/roundToOne' // Round
import store from '@/store'
import noUiSlider from 'nouislider' // noUiSlider lib
import chroma from 'chroma-js'
import { mapGetters, mapMutations, mapActions } from 'vuex'

export default {
  name: 'Risk',
  // Before route enter ------------------------------------------------- [Hook]
  async beforeRouteEnter (to, from, next) {
    if (from.name === 'activation') return next()
    const { status, errors } = await store.dispatch('moduleUser/fetchRiskPoints')
    if (status) {
      next()
    } else {
      store.commit('moduleWizard/SET_NAV_FIELD', { f: 'errorTextNext', v: errors.global[0] })
    }
  },
  // Created ------------------------------------------------------------ [Hook]
  created () {
    this.SET_CURRENT_VIEW({ vm: this })
  },
  // Mounted ------------------------------------------------------------ [Hook]
  mounted () {
    this.riskSliderInit() // Init risk slider
  },
  // Before destroy ----------------------------------------------------- [Hook]
  beforeDestroy () {
    this.$refs.riskSliderElement.noUiSlider.off('.riskSlider')
  },
  // Data ----------------------------------------------------------------------
  data () {
    return {
      tooltips: _cfg.text.tooltips,
      text: _cfg.text.riskStrategy,
      //
      riskMiddle: 0,

      riskLimitClosest: 0, // inits after slider
      riskDefault: 0, // inits after slider
      fakeRisk: {
        R: 50
      }
    }
  },
  // Watch ---------------------------------------------------------------------
  watch: {
    // If onlyBonds checked
    checkOnlyBondsModel: {
      handler (v) {
        this.onlyBondsRisksSlider(v)
      }
    }
  },
  // Computed ------------------------------------------------------------------
  computed: {
    // Vuex map getters of the User SM
    ...mapGetters('moduleUser', [
      'getPortfolioData',
      'getRiskRangeTitle'
    ]),
    // Check only bonds model
    checkOnlyBondsModel: {
      get () { return this.getPortfolioData.checkOnlyBonds },
      set (v) { this.SET_PORTFOLIO_FIELD({ f: 'checkOnlyBonds', v }) }
    },
    // Risk model
    riskModel: {
      get () { return this.getPortfolioData.risk },
      set (v) { this.SET_PORTFOLIO_FIELD({ f: 'risk', v }) }
    },
    // Current risk type
    curRiskType () {
      return this.getRiskRangeTitle(this.fakeRisk.R)
    },
    // Calculated PRoL
    calcPRoL () {
      return roundToOne(this.fakeRisk.PRoL)
    },
    // Calculated PRoR
    calcPRoR () {
      return roundToOne(this.fakeRisk.PRoR)
    },
    // Calculate risk limit
    calculateRiskLimit () {
      return this.getPortfolioData.riskMax
    },
    // Is out of risk limit
    isOutOfRiskLimit () {
      return 0
    },
    // Robot message
    robotMessage () {
      const branded = this.$store.state.config.branded
      if (branded === 'anderida' || branded === 'ds' || branded === 'scapital') return 'Инвестиции на бирже это всегда риск. Используйте форму ниже для определения удобного вам соотношения риска и дохода. Оно будет изменяться в зависимости от положения ползунка.'
      return 'Точная настройка доходности портфеля...'
    }
  },
  // Methods
  methods: {
    // Vuex map mutations of the Wizard SM
    ...mapMutations('moduleWizard', [
      'SET_CURRENT_VIEW'
    ]),
    // Vuex map mutations of the User SM
    ...mapMutations('moduleUser', [
      'SET_PORTFOLIO_FIELD'
    ]),
    // Vuex map actions of the Security SM
    ...mapActions('moduleSecurity', [
      'sendMessage'
    ]),
    // Risk slider init
    riskSliderInit () {
      const profits = this.getPortfolioData.profits
      const riskLimit = this.getPortfolioData.riskLimit
      const range = {} // New risks slider range param
      let middle // Middle point
      let start // start point
      const findStart = profits.map(p => p.R) // Array of values for finding closest point

      // Set 'min' & 'max' risks slider range values
      range.min = profits[0].R
      range.max = profits[profits.length - 1].R

      // Get closest values to previous
      const closestRight = findStart.find(v => v > 50)
      const closestLeft = Math.max(...findStart.filter(v => v <= 50))

      // Compare ABS of subtraction closest and middle
      if (Math.abs(closestRight - 50) > Math.abs(closestLeft - 50)) {
        middle = closestLeft
      } else {
        middle = closestRight
      }

      // Set risk limit closest
      this.riskLimitClosest = Math.max(...findStart.filter(v => v <= riskLimit))
      this.riskDefault = this.riskLimitClosest > middle ? middle : this.riskLimitClosest

      // Check if risk already exists
      if (this.riskModel) {
        start = this.riskModel.R
        for (let i = 0; i < profits.length - 1; i++) {
          if (profits[i].R === middle) {
            range['50%'] = profits[i].R
          } else {
            if (i === 0) continue
            range[profits[i].R + '%'] = profits[i].R
          }
        }
      } else {
        start = this.checkOnlyBondsModel ? 0 : this.riskDefault
        for (let i = 0; i < profits.length - 1; i++) {
          if (profits[i].R === this.riskDefault) {
            this.checkOnlyBondsModel
              ? this.riskModel = profits[0]
              : this.riskModel = profits[i]
          }
          if (profits[i].R === middle) {
            range['50%'] = profits[i].R
          } else {
            if (i === 0) continue
            range[profits[i].R + '%'] = profits[i].R
          }
        }
      }

      // Slider init
      noUiSlider.create(this.$refs.riskSliderElement, {
        snap: true,
        connect: [false, false],
        start: start,
        range: range
      })

      // If Only bounds set
      if (this.checkOnlyBondsModel) {
        this.$refs.riskSliderElement.setAttribute('disabled', true)
      }

      // Update event (fires every handle slide)
      this.$refs.riskSliderElement.noUiSlider.on('update.risksSlider', (values, handle, unencoded, tap, positions) => {
        this.fakeRisk = profits.find(x => x.R === unencoded[0])
        this.changeHandleColor(positions)
      })

      // Register slider slide event
      this.$refs.riskSliderElement.noUiSlider.on('change.riskSlider', (value, h, unencoded, tap, pos) => {
        // console.log(value, h, unencoded, tap, pos)
        if (this.fakeRisk.R > this.riskLimitClosest) {
          this.$refs.riskSliderElement.noUiSlider.set(this.riskLimitClosest)
        }
      })
      // Register slider slide event
      this.$refs.riskSliderElement.noUiSlider.on('slide.riskSlider', (value, h, unencoded, tap, pos) => {
        // console.log(value, h, unencoded, tap, pos)
        if (this.fakeRisk.R > this.riskLimitClosest) {
          this.$refs.riskSliderElement.noUiSlider.set(this.riskLimitClosest)
        }
      })

      // Set event (fires only if handle stop sliding)
      this.$refs.riskSliderElement.noUiSlider.on('set.risksSlider', (values, handle, unencoded, tap, positions) => {
        if (this.fakeRisk.R !== this.riskModel.R) {
          this.riskModel = profits.find(x => x.R === unencoded[0])
        }
      })
    },
    // Only bonds risk slider logic
    onlyBondsRisksSlider (checkBoxStatus) {
      if (checkBoxStatus) {
        this.$refs.riskSliderElement.noUiSlider.set(this.getPortfolioData.profits[0].R)
        this.$refs.riskSliderElement.setAttribute('disabled', true) // true triggers 'set' event
      } else {
        this.$refs.riskSliderElement.noUiSlider.set(this.riskDefault)
        this.$refs.riskSliderElement.removeAttribute('disabled')
      }
    },
    // Change handle color
    changeHandleColor (positions) {
      const currentPosition = positions[0] / 100
      const colorRed = '#FF441A'
      const colorGreen = '#67BC1C'
      const colorMixed = chroma.mix(colorGreen, colorRed, currentPosition).css()
      this.$refs.riskSliderElement.querySelector('.noUi-handle').style.background = colorMixed
    },
    // Self back validator
    backSelf () {
      return true
    },
    // Self next validator
    async nextSelf () {
      this.sendMessage({ e: 'RiskComplete' })
      return true // If all is fine => true, else => false
    }
  }
}
</script>
